import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type CircleIconButtonSize = 'sm' | 'default' | 'lg' | 'composer' | 'header' | 'touch' | 'toolbar';

interface CircleIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  ariaLabel: string;
  asChild?: boolean;
  size?: CircleIconButtonSize;
  /**
   * Visible hover label. Icon-only buttons are opaque to sighted users —
   * `ariaLabel` only reaches screen readers — so any icon whose meaning isn't
   * universal (hide, flag, …) should set this so the action is discoverable
   * before it's clicked. Omit it when the icon is self-evident.
   */
  tooltip?: React.ReactNode;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * `'touch'` grows the CLICKABLE area to 44px without growing the circle.
   *
   * The size scale above already carries the note that "mobile tap comfort
   * comes from spacing and layout, not making every icon-only affordance a
   * 44px filled circle" — but a top bar has no spacing to spare, so surfaces
   * there kept reaching for `size="touch"`, which makes the CIRCLE 44px. That
   * is a visible cost, and it was measured: the two Brief full-dialogs' top
   * bars ran **69px at 390x844 while every other full dialog in the app ran
   * 57px**, because their `⋯` was a 44px circle where the ✕ two hundred pixels
   * away was 32px. One bar, two control diameters, and a 12px height nobody
   * chose.
   *
   * The recipe is not new — `full-screen-split-dialog.tsx` hand-rolled exactly
   * this pseudo-element on its own ✕. It lives here now so a bar can hold the
   * small circle and the big target without either caller retyping five
   * `before:` classes, which is how the two would have drifted.
   */
  hitArea?: 'default' | 'touch';
}

// Visual size stays compact; use the semantic `composer` size only for the
// high-frequency chat action. Mobile tap comfort comes from spacing and layout,
// not making every icon-only affordance a 44px filled circle.
const SIZE_CLASSES: Record<CircleIconButtonSize, string> = {
  sm: 'size-circle-icon-sm-mobile md:size-circle-icon-sm-desktop [&_svg]:size-4 md:[&_svg]:size-3.5',
  default:
    'size-circle-icon-default-mobile md:size-circle-icon-default-desktop [&_svg]:size-5 md:[&_svg]:size-4',
  lg: 'size-circle-icon-lg-mobile md:size-circle-icon-lg-desktop [&_svg]:size-5 md:[&_svg]:size-4',
  composer:
    'size-composer-icon-mobile md:size-composer-icon-desktop [&_svg]:size-5 md:[&_svg]:size-4',
  // Page-header nav/action circles — the SAME token `PageHeader.tsx`'s
  // hamburger (`SidebarTrigger` via `size="nav-icon"`) and back-arrow use.
  // Was a recurring source of drift: header actions built with a bare
  // `default`-sized CircleIconButton (36px mobile) read visibly smaller
  // than the hamburger (40px) right next to them across the title. Any
  // icon rendered inside a `PageHeaderManager` route-config `actions:`
  // block should use this size, not `default`. The glyph size must also
  // match `nav-icon`'s own icon ratio (`size-5 md:size-4`, from button.tsx)
  // — a follow-up drift where the circle got unified to 40px but the SVG
  // inside was left at the old `size-4 md:size-3.5`, so the back-arrow
  // glyph read visibly smaller than the hamburger glyph despite sitting in
  // an identically-sized circle (caught live 2026-07-08).
  header: 'size-nav-icon-mobile md:size-nav-icon-desktop [&_svg]:size-5 md:[&_svg]:size-4',
  // Compact list-toolbar action with a true 44px mobile hit target. This is a
  // size variant (rather than a className override) so the default 36px class
  // is never emitted and cannot win later in Tailwind's compiled CSS order.
  touch: 'size-touch-min md:size-circle-icon-default-desktop [&_svg]:size-5 md:[&_svg]:size-4',
  // Responsive data-table toolbar control: true 44px circle on compact
  // screens, then the 32px desktop circle as soon as the labelled search and
  // reporting controls return at `sm`.
  toolbar: 'size-touch-min sm:size-circle-icon-sm-desktop [&_svg]:size-5 sm:[&_svg]:size-3.5',
};

/**
 * A transparent 44px square centred on the circle. `inset-1/2` + the two
 * translates centre it regardless of the circle's own size, so it holds at
 * every entry in SIZE_CLASSES and at every breakpoint those entries switch on.
 * `content-['']` is what makes the pseudo-element exist at all.
 */
const HIT_AREA_TOUCH =
  "relative before:absolute before:inset-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']";

export const CircleIconButton = React.forwardRef<HTMLButtonElement, CircleIconButtonProps>(
  (
    {
      active,
      ariaLabel,
      asChild = false,
      className,
      size = 'default',
      hitArea = 'default',
      tooltip,
      tooltipSide = 'top',
      type,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const button = (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? 'button')}
        aria-label={ariaLabel}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full bg-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          SIZE_CLASSES[size],
          hitArea === 'touch' && HIT_AREA_TOUCH,
          active
            ? 'text-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          className,
        )}
        {...props}
      >
        {children}
      </Comp>
    );

    if (tooltip == null) return button;

    // The provider is OURS, not the caller's. Radix throws
    // "`Tooltip` must be used within `TooltipProvider`" outright when one is
    // missing, so without this the `tooltip` prop is a loaded gun: it works on
    // surfaces that happen to sit under an app-level provider and crashes the
    // render anywhere else — including in tests, which mount the component
    // bare. `SegmentedToggle` already wraps its own provider for exactly this
    // reason; this primitive did not, and adding a tooltip to the /campaigns
    // toolbar buttons on 2026-08-23 turned that latent gap into two failing
    // suites. Nesting providers is supported and cheap.
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
CircleIconButton.displayName = 'CircleIconButton';
